import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { TUser } from "~/components";
import { scopelyId } from "../services/scopely/scopelyId";
import { updateAnalyticsUser } from "../services/scopely/titanAnalytics";
import type { TUserSession } from "./type/TUserSessionApi";

interface TLogoutParams {
  isScopely?: boolean;
  accountNotLinked?: boolean;
}
interface AuthState {
  user: TUserSession | null;
  userData: TUser | null;
  loginDate: Date | null;
  isAuthenticated: boolean;
  token: string | null;
  found: boolean | null;
  setAuth: (user: TUserSession, token: string) => void;
  setUser: (user: TUserSession) => void;
  setUserData: (userData: TUser) => void;
  updateUser: (userData: Partial<TUserSession>) => void;
  setToken: (token: string) => void;
  setAccountNotFound: (found: boolean) => void;
  setAccountLogin: (type: string) => void;
  modelOpenNotLogged: boolean;
  setModelOpenNotLogged: (modelOpenNotLogged: boolean) => void;
  logout: (params: TLogoutParams) => void;
  clearAuth: () => void;
  loginType: string;
}

const initialState = {
  user: null,
  userData: null,
  loginDate: null,
  isAuthenticated: false,
  token: null,
  found: false,
  modelOpenNotLogged: false,
  loginType: "scopely",
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setAuth: (user: TUserSession, token: string) => set({ user, token, isAuthenticated: true }),

      setUser: (user: TUserSession) => set({ user, isAuthenticated: true, loginDate: new Date() }),

      setUserData: (userData: TUser) => set({ userData }),

      setAccountNotFound: (found: boolean) => set({ found }),

      setAccountLogin: (type: string) => set({ loginType: type }),

      setModelOpenNotLogged: (modelOpenNotLogged: boolean) => set({ modelOpenNotLogged }),

      updateUser: (userData: Partial<TUserSession | unknown>) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),

      setToken: (token: string) => set({ token }),

      logout: async ({isScopely = false, accountNotLinked = false}) => {
        const getSSOTYPE = localStorage.getItem("login_type");
        const user = get().user;

        updateAnalyticsUser(user?.Id || 0);

        set({...initialState});
        clearAuthStorage();

        if (getSSOTYPE === "SCOPELY_IDENTITY" || isScopely) {
          const scopelyIdInitialized = await scopelyId;
          const logoutState: { accountNotLinked?: boolean} = {};
          if (accountNotLinked) {
            // send accountNotLinked flag so it can be used in logout redirect
            logoutState.accountNotLinked = true;
          }
          const encodedState = window.btoa(JSON.stringify(logoutState));
          await scopelyIdInitialized?.logout(encodedState);
        }
      },

      clearAuth: () => set({...initialState}),
    }),
    {
      name: "auth-storage", // Storage key in localStorage
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        loginDate: state.loginDate,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

function clearAuthStorage() {
  localStorage.removeItem("sessionToken");
  localStorage.removeItem("loginDate");
  localStorage.removeItem("login_type");
  localStorage.removeItem("sent_sys_connect");
}

// Optional: Type-safe selector hooks
export const useUser = () => useAuthStore((state) => state.user);
export const useUserData = () => useAuthStore((state) => state.userData);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthToken = () => useAuthStore((state) => state.token);
export const useAccountNotFound = () => useAuthStore((state) => state.found);
export const useLogout = () => useAuthStore((state) => state.logout);
